import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { addDays, format, isSameDay, parseISO } from "date-fns"
import { LogIn, LogOut, Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { fetchGuestReminders } from "~/apis/guestReminders"
import { SOURCE_BADGES, cleansFrom, isLive, propertyColor, shortName } from "~/lib/calendar"
import CleanBadge from "~/components/calendar/CleanBadge"
import routes from "~/routes"

const REMINDER_ACTIONS = {
  checkin_instructions: "Send check-in info (Riparide)",
  welcome: "Send welcome message",
  checkout: "Send check-out note",
}

function Dot({ property }) {
  return <span className="mr-1.5 inline-block size-2 shrink-0 rounded-full" style={{ background: propertyColor(property) }} />
}

// What Jeremy needs to do about one arrival or departure. Airbnb messages its
// own guests; Riparide and direct stays get a button for whichever reminder is
// due (or a tick once it has been sent).
function Action({ booking, reminders, movement }) {
  if (booking.source === "airbnb") return <span className="text-xs text-muted-foreground">Airbnb messages the guest</span>

  const wanted = movement === "in" ? [ "checkin_instructions", "welcome" ] : [ "checkout" ]
  const mine = reminders.filter((r) => r.booking_id === booking.id && wanted.includes(r.kind))
  const due = mine.find((r) => r.status !== "done" && r.status !== "dismissed" && r.overdue)
  const pending = mine.find((r) => r.status !== "done" && r.status !== "dismissed")

  if (due) {
    return (
      <Button asChild size="sm" className="h-7 bg-brand text-brand-foreground hover:bg-brand/90">
        <Link to={`${routes.sendMessage(booking.id)}?kind=${due.kind}`}>{REMINDER_ACTIONS[due.kind]}</Link>
      </Button>
    )
  }
  if (!booking.guest_phone && !(due && due.kind === "checkin_instructions")) {
    return <Link to={routes.editBooking(booking.id)} className="text-xs font-medium text-amber-600 underline dark:text-amber-400">Add a phone number</Link>
  }
  if (pending) return <span className="text-xs text-muted-foreground">Reminder due {format(parseISO(pending.due_on), "EEE d MMM")}</span>
  return <span className="text-xs text-muted-foreground">✓ Messages sent</span>
}

function Movement({ booking, property, reminders, movement }) {
  const Icon = movement === "in" ? LogIn : LogOut
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <Icon size={14} className={movement === "in" ? "text-brand" : "text-muted-foreground"} />
      <span className="text-muted-foreground">{booking.kind === "residency" ? "Residency " : ""}{movement === "in" ? "Check-in" : "Check-out"}</span>
      <Link to={routes.bookingDetail(booking.id)} className="flex items-center font-medium hover:underline">
        <Dot property={property} />
        {booking.guest_label}
        {booking.guest_count ? ` ×${booking.guest_count}` : ""}
      </Link>
      <span className="text-xs text-muted-foreground">{shortName(property)}</span>
      <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase", SOURCE_BADGES[booking.source] || SOURCE_BADGES.direct)}>{booking.source}</span>
      {booking.needs_enrichment && <Link to={routes.editBooking(booking.id)} className="text-xs font-medium text-amber-600 underline dark:text-amber-400">needs details</Link>}
      <span className="ml-auto">
        <Action booking={booking} reminders={reminders} movement={movement} />
      </span>
    </li>
  )
}

function DayCard({ label, day, stays, cleans, properties, reminders }) {
  const propertyById = Object.fromEntries(properties.map((p) => [ p.id, p ]))
  const checkouts = stays.filter((b) => isSameDay(parseISO(b.ends_on), day))
  const checkins = stays.filter((b) => isSameDay(parseISO(b.starts_on), day))
  const dayCleans = cleans.filter((c) => isSameDay(parseISO(c.scheduled_on), day))
  const quiet = !checkouts.length && !checkins.length && !dayCleans.length

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide">
        <span className={label === "Today" ? "text-brand" : "text-muted-foreground"}>{label}</span>
        <span className="ml-1.5 font-semibold text-muted-foreground">{format(day, "EEE d MMM")}</span>
      </p>
      {quiet ? (
        <p className="text-sm text-muted-foreground">No check-ins or check-outs.</p>
      ) : (
        <ul className="space-y-1.5">
          {checkouts.map((b) => <Movement key={`out-${b.id}`} booking={b} property={propertyById[b.property_id]} reminders={reminders} movement="out" />)}
          {dayCleans.map((c) => (
            <li key={`clean-${c.id}`} className="flex items-center gap-2 text-sm">
              <Sparkles size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">Clean</span>
              <Dot property={propertyById[c.property_id]} />
              <CleanBadge clean={c} />
            </li>
          ))}
          {checkins.map((b) => <Movement key={`in-${b.id}`} booking={b} property={propertyById[b.property_id]} reminders={reminders} movement="in" />)}
        </ul>
      )}
    </div>
  )
}

// Today and tomorrow at a glance, above the calendar: who is arriving or
// leaving, which clean is on, and the guest message that is due right now.
// Unfiltered on purpose -- hiding a property in the calendar shouldn't hide
// today's check-out.
export default function TodayPanel({ properties, bookings }) {
  const [ reminders, setReminders ] = useState([])

  useEffect(() => {
    fetchGuestReminders().then(setReminders).catch(() => {})
  }, [ bookings ])

  const today = new Date()
  const stays = bookings.filter((b) => isLive(b) && b.kind !== "block")
  const cleans = cleansFrom(stays)

  return (
    <section className="mb-4 grid gap-3 md:grid-cols-2">
      <DayCard label="Today" day={today} stays={stays} cleans={cleans} properties={properties} reminders={reminders} />
      <DayCard label="Tomorrow" day={addDays(today, 1)} stays={stays} cleans={cleans} properties={properties} reminders={reminders} />
    </section>
  )
}
